import { initValidation } from '../core/validation';
import { watchEmail } from '../core/accountLookup';
import { formState } from '../core/formState';
import { verificationStep } from '../core/emailVerification';
import { signIn, VERDICT, REFUSED } from '../core/signIn';
import { submitRegistration } from '../core/submit';
import { alertText, fieldMessages } from '../core/copy';
import { showLoader, hideLoader } from '../../modules/loaders';
import {
    registrationFailed,
    signInRefused,
    invalidCode,
    wrongLoginCode,
    sendingCode,
    codeSent,
    codeNotSent
} from '../core/alerts';

/**
 * The steps a guest goes through to register from the page they are already on.
 *
 * Not the form itself — Blade draws that. This is the sequence that drives it.
 *
 * Four steps, in order: look the address up, reveal what fits the answer, create
 * the account or sign the existing one in, confirm the address. Article, chapter
 * and event take exactly these four, which is why they share this file and pass
 * only what they say — `guestSteps(messages)`, one module per door.
 *
 * A door that cannot reach an outcome simply does not export that function, and the
 * optional calls below skip it. An article has no rejected() because nobody reviews
 * a request to read one.
 *
 * Which chapter or event is being joined is not asked here: the form's action and
 * its hidden inputs carry it, written by the page.
 *
 * The button has two stages, and that is deliberate. The first click looks the
 * address up and reveals what fits the answer; the second submits. A password has
 * to be typed in between, so a click is a steadier trigger than a blur under
 * autofill, tabbing and password managers.
 */

const FORM = '#registrationForm';
const BUTTON = '#registerButton';

/**
 * Sent alongside a sign-in, not just a registration: joining is also the moment the
 * profile is filled in, and the server writes it through UserProfileData.
 */
function profile() {
    // Read from inside the form: these pages carry fields of the same name outside
    // it, and a document-wide lookup picks whichever comes first in the markup.
    const field = (selector) => $(FORM).find(selector).val();

    return {
        chapter_id: field('input[name="chapter_id"]') || '',
        event_id: field('input[name="event_id"]') || '',
        user_residence_country: field('#user_residence_country'),
        user_city: field('#user_city'),
        user_company: field('#user_company'),
        user_company_role: field('#user_company_role'),
        user_organization_type: field('#user_organization_type'),
        user_additional_information: field('#user_additional_information')
    };
}

function reload() {
    window.location.reload();
}

/**
 * The form is a modal on the page that owns the chapter or event, so there is
 * nowhere to go: reloading is what shows the new state. A pending request is the
 * exception, since the hash is what reopens the wizard.
 */
function leaveFlow(dropHash) {
    if (dropHash && ! $(FORM).find('input[name="chapter_id"]').val()) {
        window.location.href = window.location.href.split('#')[0];
        return;
    }

    window.location.reload();
}

export function guestSteps(messages) {
    const $form = $(FORM);

    if (! $form.length) {
        return;
    }

    let lookup = null;
    let auth = null;
    let registration = null;

    const say = alertText;

    // Stamped on the form by <x-registration.form>, and the only thing that tells
    // one door from another in here.

    function personsName() {
        return $form.find('#name').val() || '';
    }

    const verification = verificationStep({
        form: FORM,
        translate: say,
        onConfirmed: (approved) => messages.addressConfirmed({
            approved,
            name: personsName(),
            onContinue: () => leaveFlow(! approved)
        }),
        onInvalidCode: invalidCode
    });

    // The first stage of the two-stage button: nothing is posted yet, the address is
    // only looked up. An existing one becomes a login here rather than a refusal,
    // which is what RegistrationSpec::detectsAccount describes for these doors.
    function lookUpAddress(event) {
        event.preventDefault();

        if (! state.valid()) {
            return;
        }

        showLoader();
        lookup.check();
    }

    function signInWithPassword(event) {
        event.preventDefault();
        auth.withPassword();
    }

    function signInWithCode(event) {
        event.preventDefault();
        auth.withCode();
    }

    function register(event) {
        event.preventDefault();

        if (! registration.valid()) {
            return;
        }

        registration.send();
    }

    const state = formState({
        form: FORM,
        submitButton: BUTTON,
        labels: {
            register: say(messages.label, 'Register'),
            login: say('login_reg', 'Login')
        },
        onCheck: lookUpAddress,
        onLogin: signInWithPassword,
        onRegister: register
    });

    function reset() {
        state.reset();
        verification.stopWatching();
    }

    lookup = watchEmail($form.get(0), {
        onExisting: (data) => {
            hideLoader();
            state.existing(data);
        },
        onNew: () => {
            hideLoader();
            state.fresh();
        },
        onError: () => {
            hideLoader();
            alert(fieldMessages($form.get(0)).email_exists || 'An error occurred while checking the email.');
        }
    });

    auth = signIn({
        emailField: FORM + ' #email',
        profile,
        translate: say,
        onVerdict: (verdict, detail) => {
            if (verdict === VERDICT.signedIn) {
                messages.signedIn({
                    approved: detail.approved,
                    name: personsName(),
                    // The chapter door answers a request it could not file; every
                    // other door leaves this undefined.
                    status: detail.response && detail.response.status,
                    onContinue: reload
                });
                return;
            }

            if (verdict === VERDICT.subscriptionRequired) {
                messages.planRequired?.({ upgradeUrl: detail.upgradeUrl, onClose: reload });
                return;
            }

            if (verdict === VERDICT.rejected) {
                messages.rejected?.({ onConfirm: reload });
                return;
            }

            // Signed in, but the address was never confirmed. The modal was drawn
            // before it knew who they were, so this only surfaces now — and from here
            // it is the same screen a new account gets, without leaving the page.
            verification.begin({
                userId: detail.userId,
                address: detail.email,
                onReady: () => messages.confirmFirst?.()
            });
        },
        onRefused: (kind, message) => {
            if (kind === REFUSED.password) {
                $('#passwordError').text(message).show();
                $('#password').addClass('is-invalid');
                return;
            }

            if (kind === REFUSED.code) {
                wrongLoginCode();
                return;
            }

            signInRefused({ message, onConfirm: reload });
        }
    });

    registration = submitRegistration({
        form: FORM,
        translate: say,
        // A new account always has an address to prove, so the page goes straight to
        // the confirmation step and the door speaks over it.
        onAccepted: (response) => verification.begin({
            userId: response.user_id,
            approved: Boolean(response.is_user_approved),
            address: $form.find('#email').val(),
            onReady: () => messages.registered(response)
        }),
        onFailed: (message) => registrationFailed({ message })
    });

    function requestLoginCode(event) {
        event.preventDefault();

        sendingCode(() => {
            auth.requestCode()
                .done(() => {
                    codeSent();
                    state.awaitingLoginCode();
                    state.setSubmitHandler(signInWithCode, say('login_with_code', 'Login with code'));
                })
                .fail(codeNotSent);
        });
    }

    $(BUTTON).prop('disabled', false);

    initValidation(FORM);

    $('#email').on('input', reset);

    // Resets directly. The markup carries a hidden #resetModalButton that looks like
    // the way to do this, but nothing binds a handler to it, so clicking it does
    // nothing.
    $('#validEmailMessage').on('click', '#useAnotherEmail', (event) => {
        event.preventDefault();
        $('#email').val('').focus();
        reset();
    });

    $('#forgotPasswordLink').on('click', requestLoginCode);
    $('#password').on('keyup change', state.clearPasswordError);

    reset();
}

